class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      document.dispatchEvent(new CustomEvent('cart-drawer:rendered'));
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(() => {
  const SCRIPT = document.currentScript;
  const CONFIG_ENDPOINT = SCRIPT?.dataset.configEndpoint || window.KSCartDrawerConfigEndpoint;
  const RECOMMENDATION_ENDPOINT = SCRIPT?.dataset.recommendationEndpoint || window.KSCartDrawerRecommendationEndpoint;
  const DRAWER_SELECTOR = 'cart-drawer .drawer__inner';
  const WIDGET_SELECTOR = '[data-cart-drawer-enhancements]';
  const REFRESH_DELAY = 140;

  let lastSignature = '';
  let refreshTimer;
  let followupTimer;
  const productJsonCache = new Map();

  const cacheBustedUrl = (url) => {
    if (!url) return url;
    const separator = String(url).includes('?') ? '&' : '?';
    return `${url}${separator}_cart_drawer_ts=${Date.now()}`;
  };

  const fetchJson = (url) => {
    if (!url) return Promise.resolve(null);

    return fetch(cacheBustedUrl(url), {
      mode: 'cors',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  };

  const fetchCart = () =>
    fetch(`${routes.cart_url}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

  const getProductJsonUrl = (productUrl) => {
    if (!productUrl || productUrl === '#') return '';

    try {
      const url = new URL(productUrl, window.location.origin);
      if (!url.pathname.includes('/products/')) return '';

      url.search = '';
      url.hash = '';
      if (!url.pathname.endsWith('.js')) url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;

      return url.href;
    } catch (_error) {
      return '';
    }
  };

  const fetchProductJson = (productUrl) => {
    const url = getProductJsonUrl(productUrl);
    if (!url) return Promise.resolve(null);
    if (productJsonCache.has(url)) return productJsonCache.get(url);

    const request = fetch(url, {
      credentials: 'same-origin',
      cache: 'force-cache',
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    productJsonCache.set(url, request);
    return request;
  };

  const normalizeCurrencyCode = (value) => {
    const normalized = String(value || 'USD').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(normalized) ? normalized : 'USD';
  };

  const formatMoney = (cents, currencyCode) => {
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: normalizeCurrencyCode(currencyCode),
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const formatRecommendationPrice = (value, currencyCode) => {
    const text = String(value || '').trim();
    if (!text) return '';
    if (/[^0-9.,\s-]/.test(text)) return text;

    const amount = Number(text.replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(amount)) return text;

    return formatMoney(Math.round(amount * 100), currencyCode);
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const parseDate = (value) => {
    const text = String(value || '').trim();
    if (!text) return null;

    const dateMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const year = Number(dateMatch[1]);
      const monthIndex = Number(dateMatch[2]) - 1;
      const day = Number(dateMatch[3]);
      const date = new Date(Date.UTC(year, monthIndex, day));

      if (date.getUTCFullYear() === year && date.getUTCMonth() === monthIndex && date.getUTCDate() === day) {
        return { date, time: date.getTime() };
      }
    }

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return null;

    const date = new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()));
    return { date, time: date.getTime() };
  };

  const formatShipDate = (parsedDate) => {
    if (!parsedDate?.date) return '';

    return new Intl.DateTimeFormat(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(parsedDate.date);
  };

  const getProperty = (properties, key) => {
    if (!properties) return '';
    if (!Array.isArray(properties)) return properties[key] || '';

    const match = properties.find((property) => Array.isArray(property) && property[0] === key);
    return match ? match[1] : '';
  };

  const normalizeBoolean = (value) => {
    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  };

  const getLatestPresaleShipDateFromDom = () => {
    let latestShipDate = null;

    Array.from(document.querySelectorAll('cart-drawer [data-cart-line-presale-ship-date]')).forEach((line) => {
      const shipDate = parseDate(line.dataset.cartLinePresaleShipDate);

      if (shipDate && (!latestShipDate || shipDate.time > latestShipDate.time)) {
        latestShipDate = shipDate;
      }
    });

    return latestShipDate;
  };

  const getAppData = (configPayload, recommendationPayload) => {
    const appConfig =
      window.KSCartDrawerEnhancements ||
      window.KSCartDrawerConfig ||
      window.CartDrawerEnhancements ||
      {};

    return {
      ...(configPayload || {}),
      ...appConfig,
      cartRecommendations:
        recommendationPayload?.cartRecommendations ||
        recommendationPayload?.recommendations ||
        appConfig.cartRecommendations ||
        appConfig.recommendations ||
        window.KSCartDrawerRecommendations ||
        {},
    };
  };

  const findThreshold = (thresholds, currencyCode) =>
    thresholds.find((entry) => normalizeCurrencyCode(entry?.currencyCode) === currencyCode) || null;

  const normalizeProduct = (product) => {
    if (!product) return null;
    const image = product.imageUrl || product.image || product.featuredImage || '';
    const getImageUrl = (value) => {
      if (!value) return '';
      return typeof value === 'string' ? value : value.src || value.url || '';
    };
    const rawVariants = product.variants || product.productVariants || product.variantOptions || [];
    const variants = Array.isArray(rawVariants)
      ? rawVariants
          .map((variant) => ({
            id: variant.id || variant.variantId || variant.variant_id || '',
            title: variant.title || variant.variantTitle || variant.option1 || variant.color || variant.name || '',
            sku: variant.sku || '',
            color:
              variant.color ||
              variant.hex ||
              variant.swatchColor ||
              variant.swatch_color ||
              variant.swatch?.color ||
              '',
            imageUrl: getImageUrl(
              variant.imageUrl || variant.image || variant.featuredImage
            ),
            swatchImageUrl: getImageUrl(
              variant.swatchImage || variant.swatch_image || variant.swatch?.image
            ),
            priceText: variant.priceText || variant.price || variant.displayPrice || '',
            available: variant.available !== false,
          }))
          .filter((variant) => variant.id)
      : [];
    const firstVariant = variants.find((variant) => variant.available) || variants[0];

    return {
      title: product.title || product.productTitle || '',
      productUrl: product.productUrl || product.url || '#',
      imageUrl: getImageUrl(image),
      imageAlt: product.imageAlt || image.alt || product.title || '',
      variantId: product.variantId || product.variant_id || firstVariant?.id || product.id || '',
      variantTitle: product.variantTitle || product.firstVariantTitle || product.optionTitle || firstVariant?.title || '',
      priceText: product.priceText || product.price || firstVariant?.priceText || '',
      variants,
    };
  };

  const buildState = (appData, cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const currencyCode = normalizeCurrencyCode(cart?.currency || window.Shopify?.currency?.active || 'USD');
    const freeShipping = appData.freeShipping || appData.free_shipping || {};
    const thresholds = Array.isArray(freeShipping.thresholds) ? freeShipping.thresholds : [];
    const activeThreshold = findThreshold(thresholds, currencyCode);
    const thresholdCents = Number(
      activeThreshold?.thresholdCents ||
        activeThreshold?.threshold_cents ||
        freeShipping.thresholdCents ||
        freeShipping.threshold_cents ||
        0
    );
    let latestShipDate = getLatestPresaleShipDateFromDom();

    items.forEach((item) => {
      const properties = item.properties || {};
      const shipDate = parseDate(getProperty(properties, '_notification_presale_ship_date'));

      if (shipDate && (!latestShipDate || shipDate.time > latestShipDate.time)) {
        latestShipDate = shipDate;
      }
    });

    const recommendations = Array.isArray(appData.cartRecommendations)
      ? { products: appData.cartRecommendations }
      : appData.cartRecommendations || {};
    const products = (Array.isArray(recommendations.products) ? recommendations.products : [])
      .map(normalizeProduct)
      .filter((product) => product && product.title);

    return {
      itemCount: Number(cart?.item_count) || 0,
      subtotalCents: Number(cart?.total_price) || 0,
      currencyCode,
      preorderShipDate: latestShipDate,
      freeShipping: {
        enabled: freeShipping.enabled !== false && thresholdCents > 0,
        thresholdCents,
        title: freeShipping.title || 'Free shipping',
        unlockedMessage: freeShipping.unlockedMessage || freeShipping.unlocked_message || "You've unlocked free shipping.",
        remainingMessage: freeShipping.remainingMessage || freeShipping.remaining_message || '',
      },
      recommendations: {
        enabled: products.length > 0,
        title: recommendations.title || 'Recommended for you',
        description: recommendations.description || '',
        addToCartLabel: recommendations.addToCartLabel || recommendations.add_to_cart_label || 'Add item',
        products,
      },
    };
  };

  const enrichRecommendations = (state) => {
    const products = state.recommendations.products || [];
    const needsProductJson = products.some((product) => product.variants.length <= 1 && getProductJsonUrl(product.productUrl));

    if (!needsProductJson) return Promise.resolve(state);

    return Promise.all(
      products.map((product) => {
        if (product.variants.length > 1) return Promise.resolve(product);

        return fetchProductJson(product.productUrl).then((productJson) => {
          const jsonVariants = Array.isArray(productJson?.variants) ? productJson.variants : [];
          if (jsonVariants.length <= 1) return product;

          const variants = jsonVariants
            .map((variant) => ({
              id: variant.id || '',
              title: variant.option1 || variant.public_title || variant.title || '',
              sku: variant.sku || '',
              color: variant.option1 || variant.title || '',
              imageUrl:
                variant.featured_image?.src ||
                variant.featured_image?.url ||
                productJson.featured_image ||
                product.imageUrl ||
                '',
              priceText: formatMoney(variant.price, state.currencyCode),
              available: variant.available !== false,
            }))
            .filter((variant) => variant.id);
          const selectedVariant =
            variants.find((variant) => String(variant.id) === String(product.variantId)) ||
            variants.find((variant) => variant.title === product.variantTitle) ||
            variants.find((variant) => variant.available) ||
            variants[0];

          return {
            ...product,
            imageUrl: product.imageUrl || productJson.featured_image || '',
            variantId: selectedVariant?.id || product.variantId,
            variantTitle: selectedVariant?.title || product.variantTitle,
            priceText: selectedVariant?.priceText || product.priceText,
            variants,
          };
        });
      })
    ).then((enrichedProducts) => ({
      ...state,
      recommendations: {
        ...state.recommendations,
        products: enrichedProducts,
      },
    }));
  };

  const findDrawer = () => document.querySelector(DRAWER_SELECTOR);

  const getWidget = (drawer) => drawer?.querySelector(WIDGET_SELECTOR);

  const findAnchor = (drawer) =>
    drawer?.querySelector('.drawer__cart-items-wrapper') ||
    drawer?.querySelector('#CartDrawer-CartItems') ||
    drawer?.querySelector('cart-drawer-items form');

  const ensureWidget = (drawer) => {
    if (!drawer) return null;

    let widget = getWidget(drawer);
    if (!widget) {
      widget = document.createElement('div');
      widget.className = 'cart-drawer__enhancements';
      widget.setAttribute('data-cart-drawer-enhancements', '');
    }

    const anchor = findAnchor(drawer);
    if (anchor?.parentNode) {
      anchor.parentNode.insertBefore(widget, anchor.nextSibling);
    } else {
      const footer = drawer.querySelector('.drawer__footer');
      if (footer?.parentNode) footer.parentNode.insertBefore(widget, footer);
      else drawer.appendChild(widget);
    }

    return widget;
  };

  const removeWidget = (drawer) => getWidget(drawer)?.remove();

  const renderPreorder = (state) => {
    const date = formatShipDate(state.preorderShipDate);
    if (!date) return '';

    return `
      <section class="cart-drawer__enhancement cart-drawer__preorder" aria-label="Pre-order shipping notice">
        <div class="cart-drawer__preorder-pill">
          <span class="cart-drawer__preorder-pill-dot" aria-hidden="true"></span>
          <span>Pre-order</span>
        </div>
        <p class="cart-drawer__preorder-date">Expected to ship by ${escapeHtml(date)}.</p>
      </section>
    `;
  };

  const renderFreeShipping = (state) => {
    const { freeShipping } = state;
    if (!freeShipping.enabled) return '';

    const remainingCents = Math.max(freeShipping.thresholdCents - state.subtotalCents, 0);
    const unlocked = remainingCents === 0;
    const ratio = Math.min(Math.max(state.subtotalCents / freeShipping.thresholdCents, 0), 1);
    const message = unlocked
      ? freeShipping.unlockedMessage
      : freeShipping.remainingMessage ||
        `You're ${formatMoney(remainingCents, state.currencyCode)} away from free shipping.`;

    return `
      <section class="cart-drawer__enhancement cart-drawer__free-shipping" aria-label="${escapeHtml(freeShipping.title)}">
        <div class="cart-drawer__free-shipping-pill">
          <span class="cart-drawer__free-shipping-pill-dot" aria-hidden="true"></span>
          <span>${escapeHtml(freeShipping.title)}</span>
        </div>
        <p class="cart-drawer__free-shipping-message">${escapeHtml(message)}</p>
        <div class="cart-drawer__progress" aria-hidden="true">
          <span class="cart-drawer__progress-bar" style="width: ${(ratio * 100).toFixed(2)}%"></span>
        </div>
        <div class="cart-drawer__free-shipping-meta" aria-hidden="true">
          <span>${escapeHtml(formatMoney(state.subtotalCents, state.currencyCode))}</span>
          <span>${escapeHtml(formatMoney(freeShipping.thresholdCents, state.currencyCode))}</span>
        </div>
      </section>
    `;
  };

  const renderRecommendations = (state) => {
    const { recommendations } = state;
    if (!recommendations.enabled) return '';

    const products = recommendations.products
      .map((product, productIndex) => {
        const selectedVariant = product.variants.find((variant) => String(variant.id) === String(product.variantId)) || product.variants[0];
        const selectedImageUrl = selectedVariant?.imageUrl || product.imageUrl;
        const selectedPriceText = selectedVariant?.priceText || product.priceText;
        const image = selectedImageUrl
          ? `<img class="cart-drawer__recommendation-image" src="${escapeHtml(selectedImageUrl)}" alt="${escapeHtml(
              product.imageAlt || product.title
            )}" loading="lazy">`
          : '';
        const price = formatRecommendationPrice(selectedPriceText, state.currencyCode);
        const variantPicker =
          product.variants.length > 1
            ? `
              <fieldset class="cart-drawer__recommendation-variants">
                <legend class="cart-drawer__recommendation-variant-label">
                  Color: <span data-cart-drawer-rec-selected>${escapeHtml(selectedVariant?.title || '')}</span>
                </legend>
                <div class="cart-drawer__recommendation-swatches">
                  ${product.variants
                    .map((variant, variantIndex) => {
                      const inputId = `CartDrawerRec-${productIndex}-${variant.id}`;
                      const swatchStyle = getRecommendationSwatchStyle(variant);

                      return `
                        <input
                          class="cart-drawer__recommendation-swatch-input"
                          type="radio"
                          id="${escapeHtml(inputId)}"
                          name="cart-drawer-rec-${productIndex}"
                          value="${escapeHtml(variant.id)}"
                          data-cart-drawer-rec-variant
                          data-variant-title="${escapeHtml(variant.title || '')}"
                          data-variant-image="${escapeHtml(variant.imageUrl || product.imageUrl || '')}"
                          data-variant-price="${escapeHtml(formatRecommendationPrice(variant.priceText || product.priceText, state.currencyCode))}"
                          ${String(variant.id) === String(selectedVariant?.id) ? 'checked' : ''}
                          ${variant.available ? '' : 'disabled'}
                        >
                        <label
                          class="cart-drawer__recommendation-swatch-label${variant.available ? '' : ' is-disabled'}"
                          for="${escapeHtml(inputId)}"
                          title="${escapeHtml(variant.title || variant.sku || '')}"
                          ${swatchStyle ? `style="${swatchStyle}"` : ''}
                        >
                          <span class="visually-hidden">${escapeHtml(variant.title || variant.sku || '')}</span>
                        </label>
                      `;
                    })
                    .join('')}
                </div>
              </fieldset>
            `
            : '';
        const action = product.variantId
          ? `<button class="button button--secondary cart-drawer__recommendation-button" type="button" data-cart-drawer-add-variant="${escapeHtml(
              selectedVariant?.id || product.variantId
            )}">${escapeHtml(recommendations.addToCartLabel)}</button>`
          : `<a class="button button--secondary cart-drawer__recommendation-button" href="${escapeHtml(
              product.productUrl
            )}">View</a>`;

        return `
          <article class="cart-drawer__recommendation">
            <a class="cart-drawer__recommendation-media" href="${escapeHtml(product.productUrl)}">${image}</a>
            <div class="cart-drawer__recommendation-info">
              <a class="cart-drawer__recommendation-title" href="${escapeHtml(product.productUrl)}">${escapeHtml(
                product.title
              )}</a>
              ${product.variantTitle && product.variants.length <= 1 ? `<p class="cart-drawer__recommendation-meta">${escapeHtml(product.variantTitle)}</p>` : ''}
              ${variantPicker}
              ${price ? `<p class="cart-drawer__recommendation-price" data-cart-drawer-rec-price>${escapeHtml(price)}</p>` : ''}
              ${action}
            </div>
          </article>
        `;
      })
      .join('');

    return `
      <section class="cart-drawer__enhancement cart-drawer__recommendations" aria-label="${escapeHtml(
        recommendations.title
      )}">
        <div class="cart-drawer__recommendations-header">
          <p class="cart-drawer__enhancement-title">${escapeHtml(recommendations.title)}</p>
          ${recommendations.description ? `<p class="cart-drawer__enhancement-text">${escapeHtml(recommendations.description)}</p>` : ''}
        </div>
        <div class="cart-drawer__recommendations-list">${products}</div>
        <p class="cart-drawer__recommendation-feedback" data-cart-drawer-recommendation-feedback role="status"></p>
      </section>
    `;
  };

  const getRecommendationSwatchStyle = (variant) => {
    const color = normalizeSwatchColor(variant.color || variant.title);
    if (color) return `--recommendation-swatch: ${escapeHtml(color)}`;

    const image = variant.swatchImageUrl;
    return image ? `--recommendation-swatch: url(&quot;${escapeHtml(image)}&quot;)` : '';
  };

  const normalizeSwatchColor = (value) => {
    const text = String(value || '').trim();
    if (!text) return '';
    if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text)) return text;
    if (/^(rgb|hsl)a?\(/i.test(text)) return text;

    const namedColors = {
      black: '#111111',
      white: '#ffffff',
      gray: '#6f7378',
      grey: '#6f7378',
      green: '#456348',
      moss: '#456348',
      'moss green': '#456348',
      titanium: '#6f7378',
      silver: '#b8b8b8',
      blue: '#315c88',
      orange: '#f6911e',
    };
    return namedColors[text.toLowerCase()] || '';
  };

  const render = (state) => {
    const drawer = findDrawer();
    if (!drawer) return;

    if (state.itemCount === 0) {
      removeWidget(drawer);
      return;
    }

    const html = [renderPreorder(state), renderFreeShipping(state), renderRecommendations(state)].filter(Boolean).join('');

    if (!html) {
      removeWidget(drawer);
      return;
    }

    const widget = ensureWidget(drawer);
    if (widget) widget.innerHTML = html;
  };

  const refresh = () => {
    const drawer = findDrawer();
    if (!drawer) {
      lastSignature = '';
      return;
    }

    Promise.all([fetchJson(CONFIG_ENDPOINT), fetchJson(RECOMMENDATION_ENDPOINT), fetchCart()]).then(
      ([configPayload, recommendationPayload, cart]) => {
        if (!cart) return;

        enrichRecommendations(buildState(getAppData(configPayload, recommendationPayload), cart)).then((state) => {
          const signature = JSON.stringify(state);
          if (signature === lastSignature && getWidget(drawer)) return;

          lastSignature = signature;
          render(state);
        });
      }
    );
  };

  const scheduleRefresh = (delay = REFRESH_DELAY) => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, delay);
  };

  const scheduleFollowupRefresh = () => {
    window.clearTimeout(followupTimer);
    followupTimer = window.setTimeout(() => {
      lastSignature = '';
      refresh();
    }, 850);
  };

  const setRecommendationFeedback = (message) => {
    const feedback = document.querySelector('[data-cart-drawer-recommendation-feedback]');
    if (feedback) feedback.textContent = message || '';
  };

  const addRecommendedProduct = (button) => {
    const variantId = button.dataset.cartDrawerAddVariant;
    if (!variantId) return;

    const drawer = document.querySelector('cart-drawer');
    const body = {
      items: [{ id: Number(variantId), quantity: 1 }],
    };

    if (drawer?.getSectionsToRender) {
      body.sections = drawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    button.disabled = true;
    setRecommendationFeedback('Adding...');

    fetch(routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((response) =>
        response.json().then((payload) => {
          if (!response.ok) throw new Error(payload?.description || 'Unable to add this item.');
          return payload;
        })
      )
      .then((payload) => {
        if (drawer?.renderContents && payload?.sections) drawer.renderContents(payload);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-recommendations',
            cartData: payload,
            productVariantId: variantId,
          });
        }
        setRecommendationFeedback('Added.');
        lastSignature = '';
        scheduleRefresh(80);
        scheduleFollowupRefresh();
      })
      .catch((error) => {
        setRecommendationFeedback(error instanceof Error ? error.message : 'Unable to add this item.');
      })
      .finally(() => {
        button.disabled = false;
      });
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[data-cart-drawer-add-variant]');
    if (!button) return;

    event.preventDefault();
    addRecommendedProduct(button);
  });

  document.addEventListener('change', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const input = target?.closest('[data-cart-drawer-rec-variant]');
    if (!input) return;

    const recommendation = input.closest('.cart-drawer__recommendation');
    const button = recommendation?.querySelector('[data-cart-drawer-add-variant]');
    const selectedLabel = recommendation?.querySelector('[data-cart-drawer-rec-selected]');
    const image = recommendation?.querySelector('.cart-drawer__recommendation-image');
    const price = recommendation?.querySelector('[data-cart-drawer-rec-price]');

    if (button) button.dataset.cartDrawerAddVariant = input.value;
    if (selectedLabel) selectedLabel.textContent = input.dataset.variantTitle || '';
    if (image && input.dataset.variantImage) image.src = input.dataset.variantImage;
    if (price && input.dataset.variantPrice) price.textContent = input.dataset.variantPrice;
  });

  document.addEventListener('cart-drawer:rendered', () => {
    lastSignature = '';
    scheduleRefresh(80);
  });

  document.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('cart-drawer')) {
      scheduleRefresh();
      scheduleFollowupRefresh();
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.closest('cart-drawer')) {
      scheduleRefresh();
      scheduleFollowupRefresh();
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      lastSignature = '';
      scheduleRefresh(80);
      scheduleFollowupRefresh();
    });
  }

  window.refreshCartDrawerEnhancements = () => {
    lastSignature = '';
    scheduleRefresh(0);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh());
  } else {
    refresh();
  }
})();
